import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatVnd, formatDateTime } from '@/lib/format';
import { ChartCard } from './chart-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface RecentAlertsProps {
  department?: string;
}

const SEVERITY_VARIANT: Record<'high' | 'medium' | 'low', 'destructive' | 'warning' | 'default'> = {
  high: 'destructive',
  medium: 'warning',
  low: 'default',
};

export function RecentAlerts({ department }: RecentAlertsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-recent-alerts', department ?? null],
    queryFn: () =>
      api.listAlerts({
        severity: 'high',
        limit: 5,
        ...(department ? { department } : {}),
      }),
  });
  const rows = data?.rows ?? [];

  const alertsHref = `/alerts?severity=high${
    department ? `&department=${encodeURIComponent(department)}` : ''
  }`;

  return (
    <ChartCard
      title="Recent high alerts"
      description="5 cảnh báo mới nhất"
      contentClassName="px-4 pb-5"
    >
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
          No open high alerts 🎉
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={alertsHref}
                className="block rounded-md border border-border bg-card p-2 transition-colors hover:bg-background"
              >
                <div className="flex items-center gap-2 text-xs">
                  <Badge
                    variant={SEVERITY_VARIANT[r.severity]}
                    className="shrink-0 font-mono text-[10px] uppercase"
                  >
                    {r.severity}
                  </Badge>
                  <span className="flex-1 truncate font-medium text-foreground">
                    {r.merchant_canonical ?? r.summary}
                  </span>
                  <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                    {formatVnd(r.amount_vnd)}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground/70">
                  <span className="font-mono">{r.bank_code}</span>
                  <span>·</span>
                  <span className="font-mono">{formatDateTime(r.occurred_at)}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </ChartCard>
  );
}
